import React, { useRef, useState } from 'react';
import {
  Box, IconButton, Typography, CircularProgress, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ShareIcon from '@mui/icons-material/Share';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import AppFiles from './AppFiles';
import UppdragPicker from './UppdragPicker';
import ShareModal from './ShareModal';
import SharesTab from './SharesTab';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useApplabBridge } from '../hooks/useApplabBridge';
import { useAuth } from '../../../contexts/AuthContext';
import { APPLAB_ACCENT, APPLAB_ORANGE, CATEGORIES } from '../constants';

const APP_CATEGORIES = CATEGORIES.filter(c => c !== 'Alla');

const SANDBOX_BASE = 'https://applab.lagbasn.se';

const STATUS_OPTIONS = [
  { value: 'beta',        label: 'Beta' },
  { value: 'active',      label: 'Stabil' },
  { value: 'experiment',  label: 'Experiment' },
  { value: 'coming_soon', label: 'Vilande' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white', bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: APPLAB_ACCENT },
    '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
  },
  '& label': { color: '#888' },
  '& label.Mui-focused': { color: APPLAB_ACCENT },
};

export default function AppViewer({ app, onClose }) {
  const iframeRef = useRef(null);
  const { currentUser } = useAuth();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [showFiles, setShowFiles]         = useState(false);
  const [showShares, setShowShares]       = useState(false);
  const [shareOpen, setShareOpen]         = useState(false);

  const [editOpen, setEditOpen]       = useState(false);
  const [editVals, setEditVals]       = useState({});
  const [saving, setSaving]           = useState(false);
  const [aiEmoji, setAiEmoji]         = useState(false);
  const [aiCategory, setAiCategory]   = useState(false);
  const [newFile, setNewFile]         = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [uploadErr, setUploadErr]     = useState(null);
  const fileInputRef                  = useRef();

  useApplabBridge({ iframeRef, active: !!app });

  if (!app) return null;

  const src = `${SANDBOX_BASE}/apps/${app.id}/`;

  // ── Delete ──────────────────────────────────────────────────────────────────
  async function handleDelete() {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    try {
      const token = await currentUser.getIdToken();
      await fetch('/.netlify/functions/applab-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ appId: app.id }),
      });
      onClose();
    } catch (e) {
      console.error('Delete failed', e);
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  // ── Edit ────────────────────────────────────────────────────────────────────
  function openEdit() {
    setEditVals({
      name:     app.name     || '',
      tagline:  app.tagline  || '',
      status:   app.status   || 'beta',
      emoji:    app.emoji    || '📦',
      category: app.category || 'Verktyg',
      uppdrag:  app.uppdrag  || [],
    });
    setEditOpen(true);
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'applab_apps', app.id), {
        name:       editVals.name.trim() || app.name,
        tagline:    editVals.tagline.trim(),
        status:     editVals.status,
        emoji:      editVals.emoji,
        category:   editVals.category,
        uppdrag:    editVals.uppdrag || [],
        updated_at: serverTimestamp(),
      });
      setEditOpen(false);
    } catch (e) {
      console.error('Save failed', e);
    } finally {
      setSaving(false);
    }
  }

  async function suggestCategory() {
    setAiCategory(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/applab-gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          block: 'ai.chat',
          params: {
            model: 'claude-haiku-4-5',
            max_tokens: 20,
            system: `Svara BARA med ett av dessa alternativ, exakt som det skrivs: ${APP_CATEGORIES.join(', ')}. Ingenting annat.`,
            messages: [{
              role: 'user',
              content: `Vilken kategori passar bäst för en app som heter "${editVals.name}" och gör: ${editVals.tagline || 'okänt'}`,
            }],
          },
        }),
      });
      const data = await res.json();
      const suggested = (data.content || '').trim();
      if (APP_CATEGORIES.includes(suggested)) {
        setEditVals(v => ({ ...v, category: suggested }));
      }
    } catch (e) {
      console.error('Category suggestion failed', e);
    } finally {
      setAiCategory(false);
    }
  }

  async function suggestEmoji() {
    setAiEmoji(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/applab-gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          block: 'ai.chat',
          params: {
            model: 'claude-haiku-4-5',
            max_tokens: 10,
            system: 'Svara BARA med ett enda emoji-tecken, ingenting annat.',
            messages: [{
              role: 'user',
              content: `Vilket emoji passar bäst för en app som heter "${editVals.name}" och gör: ${editVals.tagline || 'okänt'}`,
            }],
          },
        }),
      });
      const data = await res.json();
      const suggested = (data.content || '').trim();
      if (suggested) setEditVals(v => ({ ...v, emoji: suggested }));
    } catch (e) {
      console.error('Emoji suggestion failed', e);
    } finally {
      setAiEmoji(false);
    }
  }

  async function handleFileUpdate() {
    if (!newFile) return;
    setUploading(true);
    setUploadErr(null);
    try {
      const token = await currentUser.getIdToken();
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(newFile);
      });
      const res = await fetch('/.netlify/functions/applab-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          appId:       app.id,
          fileName:    newFile.name,
          fileBase64,
          appName:     editVals.name,
          description: editVals.tagline,
          status:      editVals.status,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Uppladdning misslyckades');
      setNewFile(null);
      setEditOpen(false);
      // Ladda om iframen så den nya versionen visas direkt
      if (iframeRef.current) {
        const s = iframeRef.current.src;
        iframeRef.current.src = s;
      }
    } catch (e) {
      setUploadErr(e.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, bgcolor: '#111', display: 'flex', flexDirection: 'column' }}>

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1, bgcolor: '#1a1a1a',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0, minHeight: 48,
      }}>
        <IconButton
          onClick={() => { setConfirmDelete(false); onClose(); }}
          size="small" sx={{ color: '#888', '&:hover': { color: 'white' } }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{app.emoji || '📦'}</Box>

        <Typography fontWeight={700} sx={{ color: 'white', fontSize: '0.88rem' }}>
          {app.name}
        </Typography>
        {app.tagline && (
          <Typography sx={{ color: '#555', fontSize: '0.78rem', display: { xs: 'none', sm: 'block' } }}>
            · {app.tagline}
          </Typography>
        )}
        {app.version && (
          <Typography sx={{ color: '#333', fontSize: '0.7rem', fontFamily: 'monospace', display: { xs: 'none', sm: 'block' } }}>
            v{app.version}
            {app.uploaded_at?.toDate && (
              <span style={{ marginLeft: 6, fontFamily: 'inherit', opacity: 0.7 }}>
                · {app.uploaded_at.toDate().toLocaleDateString('sv-SE')}
              </span>
            )}
          </Typography>
        )}

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#333', fontSize: '0.7rem', display: { xs: 'none', md: 'block' }, userSelect: 'none' }}>
            App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
          </Typography>

          <IconButton component="a" href={src} target="_blank" rel="noopener noreferrer"
            size="small" sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}>
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </IconButton>

          {app.type === 'html_upload' && (
            <IconButton
              size="small"
              title="Ladda ner HTML"
              sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}
              onClick={async () => {
                try {
                  const res = await fetch(src);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${app.name || 'app'}.html`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  window.open(src, '_blank');
                }
              }}
            >
              <DownloadIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}

          <IconButton
            onClick={() => setShowFiles(f => !f)}
            size="small"
            sx={{ color: showFiles ? APPLAB_ACCENT : '#555', '&:hover': { color: APPLAB_ACCENT } }}
          >
            <FolderOpenIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <IconButton
            onClick={() => setShowShares(s => !s)}
            size="small"
            sx={{ color: showShares ? APPLAB_ACCENT : '#555', '&:hover': { color: APPLAB_ACCENT } }}
          >
            <BarChartIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <IconButton
            onClick={() => setShareOpen(true)}
            size="small"
            sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}
          >
            <ShareIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {app.type === 'html_upload' && (
            <>
              <IconButton onClick={openEdit} size="small" sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>

              {confirmDelete ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                  <Typography fontSize="0.78rem" sx={{ color: '#ef5350' }}>Radera?</Typography>
                  <Box onClick={handleDelete} sx={{
                    px: 1.5, py: 0.4, borderRadius: 1, cursor: 'pointer',
                    bgcolor: '#ef5350', color: 'white', fontSize: '0.75rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    '&:hover': { bgcolor: '#c62828' },
                  }}>
                    {deleting ? <CircularProgress size={12} sx={{ color: 'white' }} /> : 'Ja, radera'}
                  </Box>
                  <Box onClick={() => setConfirmDelete(false)} sx={{
                    px: 1.5, py: 0.4, borderRadius: 1, cursor: 'pointer',
                    bgcolor: 'rgba(255,255,255,0.08)', color: '#888', fontSize: '0.75rem',
                    '&:hover': { color: 'white' },
                  }}>
                    Avbryt
                  </Box>
                </Box>
              ) : (
                <IconButton onClick={handleDelete} size="small" sx={{ color: '#555', '&:hover': { color: '#ef5350' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Box component="iframe"
          ref={iframeRef}
          src={src}
          title={app.name}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          sx={{
            flex: 1, border: 'none', display: 'block', bgcolor: '#111',
            width: (showFiles || showShares) ? { xs: '0%', md: '60%' } : '100%',
            minWidth: (showFiles || showShares) ? { xs: 0, md: 0 } : undefined,
            transition: 'width 0.25s ease',
          }}
        />
        {showFiles && (
          <Box sx={{
            width: { xs: '100%', md: '40%' }, maxWidth: 420,
            borderLeft: '1px solid rgba(255,255,255,0.07)',
            flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography fontWeight={700} fontSize="0.82rem" sx={{ color: 'white' }}>Filer</Typography>
            </Box>
            <AppFiles appId={app.id} />
          </Box>
        )}
        {showShares && !showFiles && (
          <Box sx={{
            width: { xs: '100%', md: '40%' }, maxWidth: 460,
            borderLeft: '1px solid rgba(255,255,255,0.07)',
            flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography fontWeight={700} fontSize="0.82rem" sx={{ color: 'white', flex: 1 }}>Delningar</Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <SharesTab app={app} />
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Edit dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', minWidth: { xs: '90vw', sm: 440 } } }}
      >
        <DialogTitle sx={{ color: 'white', pb: 1 }}>Redigera app</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>

          {/* Emoji + AI-knapp */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 52, height: 52, borderRadius: 2, fontSize: '1.8rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              flexShrink: 0,
            }}>
              {editVals.emoji || '📦'}
            </Box>
            <TextField
              label="Emoji-ikon"
              value={editVals.emoji || ''}
              onChange={e => setEditVals(v => ({ ...v, emoji: e.target.value }))}
              size="small"
              sx={{ ...inputSx, width: 100 }}
            />
            <Button
              onClick={suggestEmoji}
              disabled={aiEmoji}
              size="small"
              startIcon={aiEmoji ? <CircularProgress size={12} sx={{ color: APPLAB_ACCENT }} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              sx={{ color: APPLAB_ACCENT, fontSize: '0.75rem', textTransform: 'none', border: `1px solid ${APPLAB_ACCENT}44`, px: 1.5 }}
            >
              {aiEmoji ? 'Frågar AI...' : 'Föreslå med AI'}
            </Button>
          </Box>

          <TextField
            label="App-namn"
            value={editVals.name || ''}
            onChange={e => setEditVals(v => ({ ...v, name: e.target.value }))}
            fullWidth size="small" sx={inputSx}
          />

          <TextField
            label="Beskrivning"
            value={editVals.tagline || ''}
            onChange={e => setEditVals(v => ({ ...v, tagline: e.target.value }))}
            fullWidth size="small" multiline rows={2} sx={inputSx}
          />

          <FormControl size="small" fullWidth>
            <InputLabel sx={{ color: '#888', '&.Mui-focused': { color: APPLAB_ACCENT } }}>Status</InputLabel>
            <Select
              value={editVals.status || 'beta'}
              onChange={e => setEditVals(v => ({ ...v, status: e.target.value }))}
              label="Status"
              sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' }, '& .MuiSvgIcon-root': { color: '#888' } }}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: 'white' } } }}
            >
              {STATUS_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value} sx={{ color: 'white' }}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel sx={{ color: '#888', '&.Mui-focused': { color: APPLAB_ACCENT } }}>Kategori</InputLabel>
              <Select
                value={editVals.category || 'Verktyg'}
                onChange={e => setEditVals(v => ({ ...v, category: e.target.value }))}
                label="Kategori"
                sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' }, '& .MuiSvgIcon-root': { color: '#888' } }}
                MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: 'white' } } }}
              >
                {APP_CATEGORIES.map(c => (
                  <MenuItem key={c} value={c} sx={{ color: 'white' }}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={suggestCategory}
              disabled={aiCategory}
              size="small"
              startIcon={aiCategory ? <CircularProgress size={12} sx={{ color: APPLAB_ACCENT }} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              sx={{ color: APPLAB_ACCENT, fontSize: '0.75rem', textTransform: 'none', border: `1px solid ${APPLAB_ACCENT}44`, px: 1.5, whiteSpace: 'nowrap' }}
            >
              {aiCategory ? 'Frågar...' : 'Föreslå'}
            </Button>
          </Box>

          <UppdragPicker
            value={editVals.uppdrag || []}
            onChange={v => setEditVals(ev => ({ ...ev, uppdrag: v }))}
            label="Kopplade uppdrag"
          />

          {/* HTML/ZIP update */}
          <Box>
            <Typography fontSize="0.75rem" sx={{ color: '#666', mb: 0.75 }}>Uppdatera HTML-fil</Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.zip"
              style={{ display: 'none' }}
              onChange={e => { setNewFile(e.target.files[0] || null); setUploadErr(null); }}
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer',
                border: `1px dashed ${newFile ? APPLAB_ACCENT : 'rgba(255,255,255,0.12)'}`,
                bgcolor: newFile ? APPLAB_ACCENT + '0d' : 'rgba(255,255,255,0.02)',
                '&:hover': { borderColor: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '0a' },
                transition: 'all 0.15s',
              }}
            >
              <UploadFileIcon sx={{ fontSize: 18, color: newFile ? APPLAB_ACCENT : '#555' }} />
              <Typography fontSize="0.8rem" sx={{ color: newFile ? 'white' : '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {newFile ? newFile.name : 'Välj .html eller .zip'}
              </Typography>
              {newFile && (
                <Typography fontSize="0.7rem" sx={{ color: '#777', flexShrink: 0 }}>
                  {(newFile.size / 1024 / 1024).toFixed(1)} MB
                </Typography>
              )}
            </Box>
            {uploading && (
              <LinearProgress sx={{ mt: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)', '& .MuiLinearProgress-bar': { bgcolor: APPLAB_ACCENT } }} />
            )}
            {uploadErr && (
              <Typography fontSize="0.75rem" sx={{ color: '#ef9a9a', mt: 0.5 }}>{uploadErr}</Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setEditOpen(false); setNewFile(null); setUploadErr(null); }} sx={{ color: '#888' }}>Avbryt</Button>
          {newFile ? (
            <Button
              onClick={handleFileUpdate}
              disabled={uploading}
              variant="contained"
              sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' } }}
            >
              {uploading ? 'Laddar upp...' : 'Ladda upp & spara'}
            </Button>
          ) : (
            <Button
              onClick={saveEdit}
              disabled={saving}
              variant="contained"
              sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' } }}
            >
              {saving ? 'Sparar...' : 'Spara'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ShareModal app={app} open={shareOpen} onClose={() => setShareOpen(false)} />
    </Box>
  );
}
